import { z } from 'zod';
import { defineEndpoint } from './contract.ts';

// Live updates (design §6.5, plan 1C Chunk 3). A browser tab holds one stream,
// GET /api/live?tab=<id>, and changes its topics with POST /api/live/subscriptions.
// Topics are declared in module manifests: `<module>.<name>`, or
// `<module>.<name>:<param>` for a topic declared with `param: true`.

/** A topic's declared name: `<module>.<name>`, lower case. */
export const TOPIC_NAME = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9_]*)+$/;

/** A topic's parameter: up to 128 letters, digits, `_`, `.` or `-`. */
export const TOPIC_PARAM = /^[A-Za-z0-9_.-]{1,128}$/;

/** A topic as subscribed: a name, with `:<param>` when it takes one. */
export const Topic = z
  .string()
  .max(256)
  .refine((topic) => parseTopic(topic) !== null, 'Topics are <module>.<name> or <module>.<name>:<param>');

/** Splits `bank.account:abc` into its name and param, or null if it isn't a topic. */
export function parseTopic(topic: string): { name: string; param: string | null } | null {
  const colon = topic.indexOf(':');
  const name = colon === -1 ? topic : topic.slice(0, colon);
  const param = colon === -1 ? null : topic.slice(colon + 1);
  if (!TOPIC_NAME.test(name)) return null;
  if (param !== null && !TOPIC_PARAM.test(param)) return null;
  return { name, param };
}

/** A tab id, made by the browser once per page load. */
export const TabId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);

/** The largest live payload, as JSON. */
export const MAX_LIVE_PAYLOAD_BYTES = 64 * 1024;

/** The most topics one tab may hold. */
export const MAX_TOPICS_PER_TAB = 100;

const WorkerStatus = z.object({
  /** Whether ITC_WORKER_TOKEN is set, so a worker can connect at all. */
  configured: z.boolean(),
  connected: z.boolean(),
});

/**
 * What the stream sends, one JSON object per SSE `data:` line. `message` and
 * `notification` carry an SSE id, `<boot>:<counter>`, so a reconnect with
 * `Last-Event-ID` gets what it missed.
 */
export const LiveStreamMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hello'), tab: z.string(), boot: z.string(), serverTime: z.iso.datetime(), worker: WorkerStatus }),
  z.object({ type: z.literal('message'), topic: z.string(), data: z.unknown() }),
  /** A new inbox item for this user, and their unread count (Chunk 5). */
  z.object({ type: z.literal('notification'), item: z.unknown(), unreadCount: z.number().int() }),
  z.object({ type: z.literal('worker'), worker: WorkerStatus }),
  /** The user lost access to a topic; nothing more arrives on it. */
  z.object({ type: z.literal('revoked'), topic: z.string(), reason: z.string() }),
  /** Missed messages can't be replayed (another boot, or past the buffer): resubscribe and fetch again. */
  z.object({ type: z.literal('resync'), reason: z.enum(['restarted', 'gap', 'unknown_tab']) }),
  z.object({ type: z.literal('ping'), serverTime: z.iso.datetime() }),
]);
export type LiveStreamMessage = z.infer<typeof LiveStreamMessage>;

export const TopicRefusal = z.object({ topic: z.string(), reason: z.enum(['unknown', 'forbidden']) });
export type TopicRefusal = z.infer<typeof TopicRefusal>;

/**
 * Changes a tab's topics. Granted topics receive every message from the moment the
 * answer is sent. A tab belongs to the session that made it.
 */
export const updateLiveSubscriptions = defineEndpoint({
  method: 'POST',
  path: '/live/subscriptions',
  permission: 'authenticated',
  // Page navigations change subscriptions often; they're the caller's own transient state.
  audit: false,
  rateLimit: { max: 240, windowMs: 60_000 },
  body: z.object({
    tab: TabId,
    add: z.array(Topic).max(100).default([]),
    remove: z.array(Topic).max(100).default([]),
  }),
  response: z.object({ granted: z.array(z.string()), denied: z.array(TopicRefusal) }),
});
